import { AlignItems, Box, Display, FlexDirection, Heading4 } from '@gousto-internal/citrus-react'
import React from 'react'

import type {
  DashboardAnalytics,
  DashboardSection,
} from '@library/api-customer-engagement-experience'

import { HeatAndEatCarousel } from './sections/HeatAndEatCarousel/HeatAndEatCarousel'
import { RecipeCarousel } from './sections/RecipeCarousel/RecipeCarousel'
import { RegularDeliveryPrompt } from './sections/RegularDeliveryPrompt/RegularDeliveryPrompt'
import { EmptyStateHero } from './sections/EmptyStateHero/EmptyStateHero'
import { EmptyStateHeroV2 } from './sections/EmptyStateHeroV2/EmptyStateHeroV2'
import { HeaderTag } from './sections/HeaderTag/HeaderTag'
import { OrderAnotherBox } from './sections/OrderAnotherBox/OrderAnotherBox'
import { OrderCard } from './sections/OrderCard/OrderCard'
import { trackAction, trackRecipeCarousel } from './unsubscribedDashboardTracking'

import { selectHeroSection } from './selectHeroSection'

import type { SaveSlotSelection } from './sections/OrderCard/OrderCard'

export type DashboardActionMeta = {
  orderId?: string
  collectionSlug?: string
  recipeUrl?: string
}

export type OrderCardActionState = {
  isSaveSlotPending: boolean
  hasSaveSlotError: boolean
  isSkipDeliveryPending: boolean
  hasSkipDeliveryError: boolean
}

// The editable upcoming-delivery actions (change slot, skip) are mutations, not
// navigation intents, so they bypass `onAction` and flow through here instead.
export type UnsubscribedDashboardOrderActions = {
  saveSlot: (orderId: string, selection: SaveSlotSelection, analytics?: DashboardAnalytics) => void
  skipDelivery: (orderId: string, analytics?: DashboardAnalytics) => void
  getOrderState: (orderId: string) => OrderCardActionState
}

export type UnsubscribedDashboardProps = {
  sections: DashboardSection[]
  onAction: (intent: string, meta?: DashboardActionMeta) => void
  orderActions?: UnsubscribedDashboardOrderActions
  isNewHeroBannerEnabled?: boolean
}

const renderSection = (
  section: DashboardSection,
  onAction: UnsubscribedDashboardProps['onAction'],
  orderActions?: UnsubscribedDashboardOrderActions,
): React.ReactNode => {
  switch (section.type) {
    case 'unsubscribed_hero':
      return (
        <EmptyStateHero
          data={section.data}
          onActionClick={(intent) => {
            const option = section.data.options.find((o) => o.action.intent === intent)
            trackAction(option?.action.analytics)
            onAction(intent)
          }}
        />
      )

    case 'unsubscribed_hero_v2':
      return (
        <EmptyStateHeroV2
          data={section.data}
          onActionClick={(intent) => {
            trackAction(section.data.cta.analytics)
            onAction(intent)
          }}
        />
      )

    case 'page_header':
      return (
        <Box
          alignItems={AlignItems.FlexStart}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={2}
        >
          <Heading4>{section.data.title}</Heading4>
          <HeaderTag data={section.data.subtitle} />
        </Box>
      )

    case 'order': {
      const orderState = orderActions?.getOrderState(section.data.id)

      return (
        <OrderCard
          data={section.data}
          onPrimaryActionClick={(intent, orderId) => {
            trackAction(section.data.primary_action?.analytics)
            onAction(intent, { orderId })
          }}
          onHelpActionClick={(intent, orderId) => {
            trackAction(section.data.help_cta?.analytics)
            onAction(intent, { orderId })
          }}
          onRecipeActionClick={(action) => {
            trackAction(action.analytics)
            // An order-card recipe tile is always order-scoped, so forward the orderId for
            // intents that route by order (e.g. `edit_recipes` → the menu). Only include
            // recipeUrl when the action actually carries one (e.g. `view_cookbook_recipe`).
            onAction(action.intent, {
              orderId: section.data.id,
              ...(action.recipe_url ? { recipeUrl: action.recipe_url } : {}),
            })
          }}
          onSaveSlotClick={(orderId, selection) =>
            orderActions?.saveSlot(
              orderId,
              selection,
              section.data.details.edit_slot_action?.analytics,
            )
          }
          onSkipDeliveryClick={(orderId) =>
            orderActions?.skipDelivery(orderId, section.data.details.skip_action?.analytics)
          }
          isSaveSlotPending={orderState?.isSaveSlotPending}
          hasSaveSlotError={orderState?.hasSaveSlotError}
          isSkipDeliveryPending={orderState?.isSkipDeliveryPending}
          hasSkipDeliveryError={orderState?.hasSkipDeliveryError}
        />
      )
    }

    case 'order_box':
      return (
        <OrderAnotherBox
          data={section.data}
          onAction={(intent) => {
            trackAction(section.data.cta.analytics)
            onAction(intent)
          }}
        />
      )

    case 'recipe_carousel': {
      const { analytics } = section.data.action
      // Tapping a recipe tile does the same navigation as "View all", but tracks recipe_click.
      const goToCollection = (isTileClick: boolean) => {
        trackRecipeCarousel(analytics, { isTileClick })
        onAction(section.data.action.intent, {
          collectionSlug: section.data.action.collectionSlug,
        })
      }

      return (
        <RecipeCarousel
          title={section.data.title}
          subtitle={section.data.subtitle}
          recipes={section.data.recipes.map((recipe) => ({
            ...recipe,
            onClick: () => goToCollection(true),
          }))}
          headerAction={{
            label: section.data.action.title,
            onClick: () => goToCollection(false),
          }}
        />
      )
    }

    case 'heat_and_eat_carousel': {
      const { data } = section
      // A meal tap and the primary CTA share the same target (new-order collection).
      const goToCollection = () => {
        onAction(data.action.intent, { collectionSlug: data.action.collectionSlug })
      }
      const secondary = data.secondaryAction

      return (
        <HeatAndEatCarousel
          tag={data.tag}
          title={data.title}
          subtitle={data.subtitle}
          meals={data.meals.map((meal) => ({
            id: meal.id,
            title: meal.title,
            imageUrl: meal.imageUrl,
            imageAlt: meal.imageAlt,
            cookTimeMins: meal.cookTimeMins,
            kcal: meal.kcal,
            onClick: () => {
              trackAction(meal.analytics)
              goToCollection()
            },
          }))}
          primaryAction={{
            title: data.action.title,
            onClick: () => {
              trackAction(data.action.analytics)
              goToCollection()
            },
          }}
          secondaryAction={
            secondary
              ? {
                  title: secondary.title,
                  onClick: () => {
                    trackAction(secondary.analytics)
                    onAction(secondary.intent, {
                      orderId: secondary.orderId,
                      collectionSlug: secondary.collectionSlug,
                    })
                  },
                }
              : undefined
          }
          onDisplayed={() => trackAction(data.displayAnalytics)}
        />
      )
    }

    case 'dashboard_action':
      return (
        <RegularDeliveryPrompt
          data={section.data}
          onAction={(intent) => {
            trackAction(section.data.analytics)
            onAction(intent)
          }}
        />
      )

    default:
      return null
  }
}

const isHeroSection = (section: DashboardSection) =>
  section.type === 'unsubscribed_hero' || section.type === 'unsubscribed_hero_v2'

export const UnsubscribedDashboard = ({
  sections,
  onAction,
  orderActions,
  isNewHeroBannerEnabled = false,
}: UnsubscribedDashboardProps) => {
  const heroSection = selectHeroSection(sections, isNewHeroBannerEnabled)
  // Skip the losing hero section rather than reordering, so any other sections keep their
  // API-provided position.
  const sectionsToRender = sections.filter(
    (section) => !isHeroSection(section) || section.type === heroSection?.type,
  )

  return (
    <Box
      data-testid="unsubscribed-dashboard"
      maxWidth="1200px"
      width="100%"
      style={{ margin: '0 auto' }}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={[4, 6]}
      paddingV={[4, 6]}
      paddingH={[3, 6, 6, 6, 0]}
    >
      {sectionsToRender.map((section, index) => {
        const content = renderSection(section, onAction, orderActions)

        return content ? <Box key={`${section.type}-${index}`}>{content}</Box> : null
      })}
    </Box>
  )
}
